import React, { ReactNode, useState, useRef, createContext, useContext, useCallback } from 'react';
import { Alert, View, Text, TouchableOpacity, Modal, StyleSheet, Platform, TextStyle } from 'react-native';
import theme from '../config/theme';

/**
 * WebAlert - Sistema de alerta temático do app com suporte a fila
 * 
 * Este componente fornece uma funcionalidade similar ao Alert.alert nativo do React Native,
 * para web e iOS, com suporte a fila para garantir que todos os alertas
 * sejam exibidos, mesmo quando vários são solicitados simultaneamente.
 */

// Tipos auxiliares
export type AlertButton = {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
};

export type AlertOptions = {
  title: string;
  message?: string;
  buttons: AlertButton[];
};

// Contexto e Provider
type AlertContextType = {
  showAlert: (title: string, message?: string, buttons?: AlertButton[]) => Promise<void>;
};

const AlertContext = createContext<AlertContextType>({ showAlert: () => Promise.resolve() });
export const useWebAlert = () => useContext(AlertContext);

export const WebAlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Fila interna de alerts
  type QueueItem = { opts: AlertOptions; resolve: () => void };
  const queueRef = useRef<QueueItem[]>([]);
  const showingRef = useRef(false);

  // Estado do alert visível
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<AlertOptions>({ title: '', message: '', buttons: [] });

  const dismissWithButton = useCallback((index: number) => {
    const button = options.buttons[index];
    if (!button) return;
    button.onPress?.();
  }, [options.buttons]);

  /**
   * Método exposto para enfileirar alertas
   * Retorna uma Promise que é resolvida quando o alerta é fechado
   */
  function showAlert(
    title: string,
    message = '',
    buttons?: AlertButton[]
  ): Promise<void> {
    return new Promise(resolve => {
      // Se não houver botões, usar um botão OK padrão
      const btns =
        buttons && buttons.length > 0
          ? buttons
          : [{ text: 'OK', onPress: () => {} }];

      if (Platform.OS === 'android') {
        Alert.alert(
          title,
          message,
          btns.map((btn) => {
            const originalOnPress = btn.onPress;
            return {
              ...btn,
              onPress: () => {
                originalOnPress?.();
                resolve();
              },
            };
          })
        );
        return;
      }
      
      // Adicionar à fila
      queueRef.current.push({ opts: { title, message, buttons: btns }, resolve });
      
      // Tentar processar a fila
      processQueue();
    });
  }

  /**
   * Processa o próximo item da fila se nenhum alerta estiver sendo exibido
   */
  function processQueue() {
    if (showingRef.current || queueRef.current.length === 0) return;
    
    // Pegar o próximo alerta da fila
    const { opts, resolve } = queueRef.current.shift()!;
    showingRef.current = true;
    setOptions(opts);
    setVisible(true);

    // Decorar cada botão para resolver a Promise e seguir na fila
    opts.buttons.forEach((btn, idx) => {
      const originalOnPress = btn.onPress;
      opts.buttons[idx] = {
        ...btn,
        onPress: () => {
          // fechar modal
          setVisible(false);
          showingRef.current = false;
          
          // chamar callback do botão
          originalOnPress && originalOnPress();
          
          // resolver a Promise
          resolve();
          
          // processar próximo item da fila (se houver)
          setTimeout(() => processQueue(), 0);
        },
      };
    });
  }

  const isThemedAlertPlatform = Platform.OS !== 'android';
  const useStackedButtons = options.buttons.length > 2;
  const cancelButtonIndex = options.buttons.findIndex(btn => btn.style === 'cancel');

  // Renderiza alerta temático no web e iOS. No Android mantemos o Alert nativo.
  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {isThemedAlertPlatform && visible && (
        <Modal
          animationType="fade"
          onRequestClose={() => dismissWithButton(cancelButtonIndex >= 0 ? cancelButtonIndex : 0)}
          presentationStyle="overFullScreen"
          transparent
          visible
        >
          <View style={styles.overlay}>
            <View style={styles.container}>
              <Text style={styles.title}>{options.title}</Text>
              {options.message !== '' && (
                <Text style={styles.message}>{options.message}</Text>
              )}
              <View style={[styles.buttonRow, useStackedButtons && styles.buttonColumn]}>
                {options.buttons.map((btn, i) => (
                  <TouchableOpacity
                    key={i}
                    style={[
                      styles.button,
                      useStackedButtons ? styles.buttonStacked : styles.buttonInline,
                      i > 0 && (useStackedButtons ? styles.buttonTopBorder : styles.buttonLeftBorder),
                    ]}
                    onPress={btn.onPress}
                  >
                    <Text
                      style={[
                        styles.buttonText,
                        btn.style === 'cancel' && styles.cancelButtonText,
                        btn.style === 'destructive' && { color: theme.colors.util.error } as TextStyle,
                      ]}
                    >
                      {btn.text}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
        </Modal>
      )}
    </AlertContext.Provider>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.overlay.semi,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: theme.zIndex.overlay,
    padding: theme.spacing.lg,
  },
  container: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    paddingTop: theme.spacing.lg,
    ...theme.shadows.lg,
  },
  title: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as TextStyle['fontWeight'],
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
    paddingHorizontal: theme.spacing.lg,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: theme.fontFamily?.medium,
      android: theme.fontFamily?.medium,
      web: theme.fontFamily?.medium
    }),
  },
  message: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    lineHeight: 22,
    marginBottom: theme.spacing.lg,
    paddingHorizontal: theme.spacing.lg,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: theme.fontFamily?.regular,
      android: theme.fontFamily?.regular,
      web: theme.fontFamily?.regular
    }),
  },
  buttonRow: {
    flexDirection: 'row',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.border.light,
  },
  buttonColumn: {
    flexDirection: 'column',
  },
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  buttonInline: {
    flex: 1,
  },
  buttonStacked: {
    width: '100%',
  },
  buttonTopBorder: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors.border.light,
  },
  buttonLeftBorder: {
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderLeftColor: theme.colors.border.light,
  },
  buttonText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.primary.default,
    fontWeight: theme.fontWeights.medium as TextStyle['fontWeight'],
    fontFamily: Platform.select({
      ios: theme.fontFamily?.medium,
      android: theme.fontFamily?.medium,
      web: theme.fontFamily?.medium
    }),
  },
  cancelButtonText: {
    color: theme.colors.primary.default,
  },
});
